import { Formik, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef } from 'react'
import { Form, Modal } from 'react-bootstrap'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { generatePassword } from '../../../../../../../util/auth.util'
import { getEmployeeNameWithEmail } from '../../../../../../../util/table.util'
import PasswordSettingsSection from '../../../../modals/common/PasswordSettingsSection'
import {
  buildInitialValues,
  FORM_FIELDS,
  getValidationSchema,
} from '../../../../modals/common/PasswordSettingsSection/fieldset'
import { cloneChildComponents } from '../../../../utils/helpers'
import { mapGwsPasswordFormValues } from '../../utils/mapGwsPasswordFormValues'

const RESET_PASSWORD_ACTION_ID = 'ENGAGE.RESET_PASSWORD_SUCCESS'

const syncResetPasswordSubmitDisabledPropTypes = {
  setDisableSubmit: PropTypes.func.isRequired,
}

function SyncResetPasswordSubmitDisabled({ setDisableSubmit }) {
  const { values } = useFormikContext()

  useEffect(() => {
    const missingManualPassword = !values[FORM_FIELDS.IS_AUTO_GENERATING_PASSWORD] && !values[FORM_FIELDS.NEW_PASSWORD]
    const missingEmployee =
      values[FORM_FIELDS.SEND_PASSWORD_ON_COMPLETION] &&
      !values[FORM_FIELDS.IS_SENDING_PASSWORD_TO_ME] &&
      !values[FORM_FIELDS.SEND_PASSWORD_TO_EMPLOYEE_EMAIL]

    setDisableSubmit(Boolean(missingManualPassword || missingEmployee))
  }, [setDisableSubmit, values])

  return null
}

SyncResetPasswordSubmitDisabled.propTypes = syncResetPasswordSubmitDisabledPropTypes

function ResetPassword(props) {
  const { children, ...rest } = props

  const {
    intl,
    action,
    initialValues,
    onActionSubmit,
    taskParameters,
    selectedEmployee,
    allAugmenttEmployees,
    setDisableSubmit,
    setCompletedActions,
  } = rest

  const formikRef = useRef()
  const autoGeneratedPassword = useMemo(() => generatePassword(), [])

  const employeeEmailOptions = useMemo(
    () =>
      (allAugmenttEmployees || [])
        .filter((item) => !item.ignored && item.email && item.email !== selectedEmployee?.email)
        .map((item) => ({
          label: getEmployeeNameWithEmail(item),
          value: item.email,
        }))
        .sort((a, b) => a?.label?.localeCompare(b?.label)),
    [allAugmenttEmployees, selectedEmployee],
  )

  const formInitialValues = useMemo(
    () =>
      buildInitialValues({
        currentUserEmail: initialValues?.email,
        autoGeneratedPassword,
      }),
    [autoGeneratedPassword, initialValues?.email],
  )

  const validationSchema = useMemo(() => getValidationSchema(intl), [intl])

  return (
    <PerfectScrollbar options={{ wheelSpeed: 2, wheelPropagation: false }} className="paginator-margin-bottom">
      <Formik
        innerRef={formikRef}
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={formInitialValues}
        onSubmit={(values) => {
          setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, RESET_PASSWORD_ACTION_ID])

          const mappedValues = mapGwsPasswordFormValues(values)
          let parameters = taskParameters

          if (typeof taskParameters === 'function') {
            parameters = taskParameters(selectedEmployee, mappedValues)
          }

          onActionSubmit(action, parameters)
        }}
      >
        {({ handleChange, setFieldValue, setFieldTouched, values }) => (
          <Form>
            <SyncResetPasswordSubmitDisabled setDisableSubmit={setDisableSubmit} />
            <Modal.Body className="sidebar-body">
              <PasswordSettingsSection
                autoGeneratedPassword={autoGeneratedPassword}
                employeeEmailOptions={employeeEmailOptions}
                fullWidth
              />
            </Modal.Body>

            {cloneChildComponents(
              {
                ...rest,
                handleChange,
                setFieldValue,
                setFieldTouched,
                values,
                formikRef,
              },
              children,
            )}
          </Form>
        )}
      </Formik>
    </PerfectScrollbar>
  )
}

const resetPasswordPropTypes = {
  children: PropTypes.node,
  intl: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  action: PropTypes.string,
  initialValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onActionSubmit: PropTypes.func,
  taskParameters: PropTypes.oneOfType([PropTypes.func, PropTypes.object]), // eslint-disable-line react/forbid-prop-types
  selectedEmployee: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  allAugmenttEmployees: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
  setDisableSubmit: PropTypes.func,
  setCompletedActions: PropTypes.func,
}

ResetPassword.propTypes = resetPasswordPropTypes

export default ResetPassword
